import { Select, type SelectProps } from "components/Select/Select"
import { Scope } from "interface/user"
import { useCallback, useEffect, useMemo } from "react"
import { useLocation, useNavigate, useParams } from "react-router"
import { useUserRole } from "services/users"

export const ScopeSelect = ({
	basePath = "",
	ariaLabel,
	...props
}: { basePath?: string; ariaLabel: string } & SelectProps) => {
	const routerParams = useParams()
	const scopeId = routerParams.scopeId
	const { pathname } = useLocation()
	const navigate = useNavigate()
	const {
		data: {
			isProvider,
			organizations = [],
			isFieldSupportAgent,
			fieldSupportTeams = [],
		} = {},
		isLoading,
	} = useUserRole()
	const scopes = useMemo<Scope[]>(
		() =>
			isProvider ? organizations : isFieldSupportAgent ? fieldSupportTeams : [],
		[fieldSupportTeams, isFieldSupportAgent, isProvider, organizations],
	)
	const selectedValue =
		scopeId && scopeId !== ":scopeId" ? scopeId : scopes[0]?.id
	const handleChange = useCallback(
		(value: string) => {
			const scope = scopes.find(({ id }) => id === value)
			if (scope) {
				const { scopeId: _, ...otherParams } = routerParams
				const hasOtherParams = Object.values(otherParams).some(
					(value) => value !== undefined,
				)
				let nextPath = `/${scope.id}${basePath}`
				if (hasOtherParams) {
					nextPath = `/${scope.id}`
				} else if (!!scopeId) {
					nextPath = pathname.replace(scopeId, scope.id)
				}

				navigate(nextPath)
			}
		},
		[basePath, navigate, routerParams, pathname, scopeId, scopes],
	)

	useEffect(() => {
		selectedValue && selectedValue !== scopeId && handleChange(selectedValue)
	}, [handleChange, scopeId, selectedValue])

	return scopes.length > 1 ? (
		<Select
			{...props}
			virtual={false}
			aria-label={ariaLabel}
			size="large"
			loading={isLoading}
			value={selectedValue}
			onChange={handleChange}
			disabled={scopes.length === 1}
			className="w-full max-w-md"
			showSearch={{ optionFilterProp: "label" }}
			options={scopes.map(({ id, name }) => ({
				value: id,
				label: name,
			}))}
		/>
	) : null
}
